import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  envoyerFichiersDepot,
  type FichierAEnvoyer,
  type SuiviEnvoiFichier,
} from '@artosera/shared';
import { Button, ZoneDepot, type FichierChoisi } from '@artosera/ui';
import { inventorierDepot, transportConsole } from '../../../lib/depots.js';
import { formaterOctets } from '../../../lib/format.js';
import styles from './Reprise.module.css';

/** Au-delà, seuls les fichiers en vol ou en échec sont listés : un dossier en compte des milliers. */
const LIGNES_MAX = 50;

const versEnvoi = (choisi: FichierChoisi): FichierAEnvoyer<Blob> => ({
  chemin: choisi.chemin,
  octets: choisi.fichier.size,
  tranche: (debut, fin) => choisi.fichier.slice(debut, fin),
});

/**
 * L'envoi d'un lot de fichiers dans un dépôt : quatre morceaux de 16 Mio en
 * vol, une barre par fichier, les coupures réessayées. Ce qui n'est pas parti
 * se reprend d'un geste ; après un rechargement, il suffit de choisir de
 * nouveau les mêmes fichiers. À la fin, l'inventaire est lancé.
 */
export function EnvoiDepot({
  idGalerie,
  depotId,
  onFin,
}: {
  idGalerie: string;
  depotId: string;
  onFin: () => void;
}): JSX.Element {
  const { t } = useTranslation('console');
  const [fichiers, setFichiers] = useState<FichierChoisi[]>([]);
  const [suivis, setSuivis] = useState<SuiviEnvoiFichier[]>([]);
  const [enCours, setEnCours] = useState(false);
  const [fini, setFini] = useState(false);
  const controleur = useRef<AbortController | null>(null);
  // Les progrès arrivent par dizaines à la seconde : l'écran se redessine au plus cinq fois.
  const tampon = useRef<SuiviEnvoiFichier[]>([]);
  const minuterie = useRef<number | null>(null);

  useEffect(() => () => controleur.current?.abort(), []);

  const publier = () => {
    minuterie.current = null;
    setSuivis([...tampon.current]);
  };

  async function lancer(lot: FichierChoisi[]): Promise<void> {
    if (lot.length === 0) return;
    controleur.current = new AbortController();
    setFichiers(lot);
    setFini(false);
    setEnCours(true);
    tampon.current = lot.map((choisi) => ({
      chemin: choisi.chemin,
      octets: choisi.fichier.size,
      envoyes: 0,
      etat: 'attente',
      erreur: null,
      raison: null,
    }));
    publier();
    try {
      const resultat = await envoyerFichiersDepot(
        lot.map(versEnvoi),
        transportConsole(idGalerie, depotId, controleur.current.signal),
        {
          signal: controleur.current.signal,
          surSuivi: (index, suivi) => {
            tampon.current[index] = suivi;
            minuterie.current ??= window.setTimeout(publier, 200);
          },
        },
      );
      tampon.current = [...resultat];
      publier();
      if (resultat.some((suivi) => suivi.etat === 'termine')) {
        await inventorierDepot(idGalerie, depotId).catch(() => undefined);
        setFini(true);
      }
    } finally {
      setEnCours(false);
      onFin();
    }
  }

  const echecs = suivis.filter((suivi) => suivi.etat === 'erreur');
  const faits = suivis.filter((suivi) => suivi.etat === 'termine').length;
  const volume = suivis.reduce((somme, suivi) => somme + suivi.octets, 0);
  const envoyes = suivis.reduce((somme, suivi) => somme + suivi.envoyes, 0);
  const visibles = suivis
    .map((suivi, index) => ({ suivi, index }))
    .filter(({ suivi }) => suivi.etat === 'envoi' || suivi.etat === 'erreur');

  return (
    <div className={styles.envoi}>
      <ZoneDepot
        desactive={enCours}
        onFichiers={(lot) => void lancer(lot)}
        libelles={{
          consigne: t('reprise.depots.zone.consigne'),
          fichiers: t('reprise.depots.zone.fichiers'),
          dossier: t('reprise.depots.zone.dossier'),
        }}
      />
      <p className={styles.discret}>{t('reprise.depots.envoi.reprise')}</p>

      {suivis.length > 0 ? (
        <div className={styles.pile} aria-live="polite">
          <p className={styles.texte}>
            {t('reprise.depots.envoi.resume', {
              faits,
              total: suivis.length,
              envoyes: formaterOctets(envoyes),
              volume: formaterOctets(volume),
            })}
          </p>
          <progress
            className={styles.barre}
            max={Math.max(volume, 1)}
            value={envoyes}
            aria-label={t('reprise.depots.envoi.enCours')}
          />
          {visibles.length > 0 ? (
            <ul className={styles.envois}>
              {visibles.slice(0, LIGNES_MAX).map(({ suivi, index }) => (
                <li key={`${String(index)}-${suivi.chemin}`} data-etat={suivi.etat}>
                  <span className={styles.chemin}>{suivi.chemin}</span>
                  <progress
                    className={styles.barre}
                    max={Math.max(suivi.octets, 1)}
                    value={suivi.envoyes}
                    aria-label={t('reprise.depots.envoi.progression', { chemin: suivi.chemin })}
                  />
                  <span className={styles.discret}>
                    {suivi.etat === 'erreur'
                      ? ((suivi.raison === null ? null : suivi.erreur) ??
                        t('reprise.depots.envoi.etats.erreur'))
                      : `${formaterOctets(suivi.envoyes)} / ${formaterOctets(suivi.octets)}`}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
          {visibles.length > LIGNES_MAX ? (
            <p className={styles.discret}>
              {t('reprise.depots.envoi.autres', { count: visibles.length - LIGNES_MAX })}
            </p>
          ) : null}
          {!enCours && echecs.length > 0 ? (
            <p className={styles.avertissement} role="status">
              {t('reprise.depots.envoi.echecs', { count: echecs.length })}
            </p>
          ) : null}
          {fini && echecs.length === 0 ? (
            <p className={styles.texte} role="status">
              {t('reprise.depots.envoi.fini')}
            </p>
          ) : null}
          <div className={styles.actions}>
            {enCours ? (
              <Button variant="ghost" onClick={() => controleur.current?.abort()}>
                {t('reprise.depots.envoi.interrompre')}
              </Button>
            ) : null}
            {!enCours && echecs.length > 0 ? (
              <Button
                onClick={() =>
                  void lancer(fichiers.filter((_, index) => suivis[index]?.etat !== 'termine'))
                }
              >
                {t('reprise.depots.envoi.reprendre')}
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
